// DI-1 Backend AIIntention adapter (intentionKey ↔ dimensions).
//
// frontend `INTENTION_DIMENSIONS` 65 entries 양방향 자산 helper.
// backend `AIIntentionService.findByKey(intentionKey)` 라우팅 진입 시
// LLM/RL-1 → dimensions → DI-1 → intentionKey → backend 호출 chain의 adapter.
//
// 의존성: RL-8 intentionLookup (findIntentionByDimensions) + INTENTION_DIMENSIONS source
//
// 본 phase pure module — backend 호출 통합은 운영 path 통합 phase.

import { INTENTION_DIMENSIONS } from '../../AiManager/Transaction/aiToolConfig.js';
import {
  findIntentionByDimensions,
  findIntentionsByDimensions,
  DIMENSION_REQUIRED,
  DIMENSION_OPTIONAL,
} from './intentionLookup.js';

/**
 * intentionKey로 dimensions 조회.
 * @param {string} intentionKey
 * @returns {object|null}
 */
export function getDimensionsByIntentionKey(intentionKey) {
  if (!intentionKey || typeof intentionKey !== 'string') return null;
  return INTENTION_DIMENSIONS[intentionKey] ?? null;
}

/**
 * dimensions partial spec → intentionKey (RL-8 best match 위임).
 * @param {object} dims
 * @returns {string|null}
 */
export function findIntentionKeyByDimensions(dims) {
  const match = findIntentionByDimensions(dims);
  return match ? match.key : null;
}

/**
 * top-K intentionKey candidates (RL-8 위임).
 * @param {object} dims
 * @param {object} [options]
 * @returns {string[]}
 */
export function findIntentionKeysByDimensions(dims, options = {}) {
  const matches = findIntentionsByDimensions(dims, options);
  return matches.map((m) => m.key);
}

/**
 * @param {string} intentionKey
 * @returns {boolean}
 */
export function isValidIntentionKey(intentionKey) {
  if (!intentionKey || typeof intentionKey !== 'string') return false;
  return Object.prototype.hasOwnProperty.call(INTENTION_DIMENSIONS, intentionKey);
}

// [P1-2 anchor 6] LLM separator 변형(+/_) 정규화 — INTENTION_DIMENSIONS 원본 key 역방향 lookup
//   원본: 'CHECK_MEMBER+ATTENDANCE_LIST_BY+PERIOD' (dimension separator '+', 단어 결합 '_')
//   변형: 'CHECK_MEMBER_ATTENDANCE_LIST_BY_PERIOD' (모두 '_')
//   normalize: '+'→'_' 후 단일 key로 매핑. 충돌 시 first-registered 우선
const _NORMALIZED_TO_ORIGINAL = Object.fromEntries(
  Object.keys(INTENTION_DIMENSIONS).map((k) => [k.replace(/\+/g, '_'), k])
);

/**
 * input intention key → INTENTION_DIMENSIONS 원본 key 해석.
 * exact match 우선, separator 변형(+/_) fallback.
 * @param {string} input
 * @returns {string|null}
 */
export function resolveIntentionKey(input) {
  if (!input || typeof input !== 'string') return null;
  if (INTENTION_DIMENSIONS[input]) return input;
  return _NORMALIZED_TO_ORIGINAL[input.replace(/\+/g, '_')] ?? null;
}

export function listIntentionKeys() {
  return Object.keys(INTENTION_DIMENSIONS);
}

export function listIntentionKeysByCategory(category) {
  if (!category || typeof category !== 'string') return [];
  const out = [];
  for (const [key, dims] of Object.entries(INTENTION_DIMENSIONS)) {
    if (dims.category === category) out.push(key);
  }
  return out;
}

/**
 * 65 entries integrity 검증 — 필수 dimension (category/action) 존재 + dims 객체 무결성.
 * @returns {{ valid: boolean, errors: Array<{key:string, reason:string}>, total: number }}
 */
export function auditIntentionDimensions() {
  const errors = [];
  const entries = Object.entries(INTENTION_DIMENSIONS);
  for (const [key, dims] of entries) {
    if (!dims || typeof dims !== 'object') {
      errors.push({ key, reason: 'dims missing or not object' });
      continue;
    }
    for (const required of DIMENSION_REQUIRED) {
      if (!dims[required] || typeof dims[required] !== 'string') {
        errors.push({ key, reason: `required dimension "${required}" missing or not string` });
      }
    }
    // modifier는 array 또는 undefined (intentionLookup이 array | [] 둘 다 허용)
    if (dims.modifier !== undefined && !Array.isArray(dims.modifier)) {
      errors.push({ key, reason: 'modifier must be array' });
    }
  }
  return {
    valid: errors.length === 0,
    errors,
    total: entries.length,
  };
}

export { DIMENSION_REQUIRED, DIMENSION_OPTIONAL };
